import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import {
  AnimationMixer,
  LoopOnce,
  Object3D,
  Object3DEventMap,
  AnimationAction,
} from "three";

/**
 * Loads a GLTF model.
 * @param url The URL or path to the GLTF file.
 * @returns A promise that resolves with the loaded GLTF scene.
 */
export async function loadGLTF(url: string): Promise<GLTF> {
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader();

    loader.load(
      url,
      (gltf) => {
        resolve(gltf);
      },
      undefined,
      (error) => reject(error)
    );
  });
}

/**
 * Plays all animations of a loaded GLTF model.
 * @param scene The Three.js scene to add the GLTF model to.
 * @param model The loaded GLTF model.
 * @param loop Whether to loop the animation (default: false).
 * @returns An object containing the AnimationMixer and a Map of AnimationActions.
 */
export function setAnimations(
  scene: Object3D<Object3DEventMap>,
  model: GLTF,
  name?: string,
  loop?: boolean
): { mixer: AnimationMixer; actions: Map<string, AnimationAction> } {
  if (name) model.scene.name = name;
  const mixer = new AnimationMixer(model.scene);
  scene.add(model.scene);

  const actions = new Map<string, AnimationAction>();

  // Check if the model has animations
  if (model.animations && model.animations.length > 0) {
    model.animations.forEach((clip) => {
      const action = mixer.clipAction(clip);
      action.setLoop(loop ? LoopOnce : LoopOnce);
      action.clampWhenFinished = true;
      actions.set(clip.name, action); // Store actions in the Map
      // action.play();
    });
  } else {
    console.warn("No animations found in this GLTF model.");
  }

  return { mixer, actions }; // Return mixer and actions
}

/**
 * Starts all animations for a given AnimationMixer.
 * @param actions A Map of AnimationActions.
 */
export function startAnimation(actions: Map<string, AnimationAction>) {
  actions.forEach((action) => {
    action.reset().play();
  });
}

/**
 * Stops all animations immediately.
 * @param actions A Map of AnimationActions.
 */
export function stopAnimation(actions: Map<string, AnimationAction>) {
  actions.forEach((action) => {
    action.stop();
  });
}

/**
 * Resets all animations to their initial state.
 * @param actions A Map of AnimationActions.
 */
export function resetAnimation(actions: Map<string, AnimationAction>) {
  actions.forEach((action) => {
    action.stop();
    action.reset();
  });
}
